import React from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "@store/store";
import { toast } from "react-toastify";

import {
  postSaveUploadedFileThunk,
  getFileSignedURLThunk,
  getDeleteSignedURLThunk,
  deleteFileByIdThunk,
  getFileROWSignedURLThunk,
} from "@modules/stepper/redux/thunk/documentThunk";

import { genericUploadThunk } from "@modules/provisional-payment/redux/thunk/uploadThunk";
import { uploadToS3Thunk } from "@store/thunk/UploadS3Thunk";
import { deleteFileOnAwsAPI } from "../../utils/s3Upload";
import { openFileBySignedUrl } from "../../utils/getViewSignedUrlHandler";
import { productionCheck } from "@components/buttonGroupView/utils";

import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import { ImagePreviewGrid } from "@modules/application-section/components/ImagePreviewGrid";
import { useSearchParams } from "react-router-dom";
import { ImagePreviewGridRow } from "@modules/application-section-row/components/ImagePreviewGridRow";

type StaticDoc = {
  document_id: number;
  document_name: string;
  upload_required?: boolean;
  media_type: string;
  uploadedFile?: any;
};

interface ClarificationUploadedDocumentsProps {
  documents: any[];
  applicationId: string;
  uploadedFiles: any;
  setUploadedFiles: any;
  forceFreshUpload?: boolean;
}

const rowStyle = (index: number) => ({
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "1fr 220px" },
  alignItems: "center",
  gap: 1.5,
  px: 1.5,
  py: 1.25,
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F7F7FA",
  borderBottom: "1px solid #ECECF1",
});

const StaticTable: React.FC<{
  title: string;
  docs: StaticDoc[];
  handleUpload: any;
  handleDelete: any;
  handleViewFile: any;
}> = ({ title, docs, handleUpload, handleDelete, handleViewFile }) => {
  const getTruncatedFileName = (fileName: string, maxLength = 15) => {
    const lastDot = fileName?.lastIndexOf(".");
    if (lastDot === -1) return fileName;

    const name = fileName?.slice(0, lastDot);
    const ext = fileName?.slice(lastDot);

    const truncated =
      name?.length > maxLength ? name?.slice(0, maxLength) + "..." : name;

    return truncated + ext;
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#212121", mb: 1.25 }}>
        Upload Documents222
      </Typography> */}

      <Box
        sx={{
          border: "1px solid #E5E7EB",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 66px" },
            px: 1.5,
            py: 1,
            backgroundColor: "#F2F1F6",
            borderBottom: "1px solid #E5E7EB",
          }}
        >
          <Typography
            sx={
              title === "Drawing type (PDF / JPEG)"
                ? {
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    fontStyle: "normal",
                    fontSize: "14px",
                    lineHeight: "100%",
                    letterSpacing: "0%",
                    color: "#000000",
                  }
                : { fontSize: "14px", color: "#000000", fontWeight: 500 }
            }
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#212121",
              fontWeight: 500,
              textAlign: { xs: "left", md: "center" },
            }}
          >
            Action
          </Typography>
        </Box>

        {docs.map((doc, index) => (
          <Box key={`${doc.document_name}-${index}`} sx={rowStyle(index)}>
            <Typography
              sx={
                title === "Drawing type (PDF / JPEG)"
                  ? {
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 400,
                      fontStyle: "normal",
                      fontSize: "14px",
                      lineHeight: "18px",
                      letterSpacing: "0%",
                      color: "#000000",
                    }
                  : { fontSize: "14px", color: "#212121", lineHeight: "22px" }
              }
            >
              {doc.document_name}
              {doc.upload_required && (
                <Box component="span" sx={{ color: "#D92D20", ml: 0.25 }}>
                  *
                </Box>
              )}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: {
                  xs: "flex-start",
                  md: "flex-end",
                },
                gap: 1,
              }}
            >
              {!doc.uploadedFile ? (
                <>
                  <input
                    hidden
                    id={`upload-${doc.document_id}`}
                    type="file"
                    accept={
                      doc.media_type === "image" ? ".jpg,.jpeg,.png" : ".pdf"
                    }
                    onChange={(e) => handleUpload(e.target.files?.[0], doc)}
                  />

                  <label htmlFor={`upload-${doc.document_id}`}>
                    <IconButton
                      component="span"
                      sx={{
                        background: "#FFFFFF",
                        borderRadius: "8px",
                        border: "1px solid #D0D5DD",
                        padding: "6px 8px",
                        "&:hover": {
                          backgroundColor: "#E7E4F8",
                        },
                      }}
                    >
                      <FileUploadOutlinedIcon />
                    </IconButton>
                  </label>
                </>
              ) : (
                <>
                  <Typography
                    sx={{
                      color: "#757575",
                      fontSize: "12px",
                      cursor: "pointer",
                      textDecoration: "underline",
                      "&:hover": {
                        color: "#3A1C73",
                      },
                    }}
                    onClick={() =>
                      handleViewFile(doc.uploadedFile.document_name)
                    }
                  >
                    {getTruncatedFileName(doc.uploadedFile.file_name)}
                  </Typography>

                  <IconButton
                    sx={{
                      background: "#FFFFFF",
                      borderRadius: "8px",
                      border: "1px solid #D0D5DD",
                      padding: "6px 8px",
                      "&:hover": {
                        backgroundColor: "#E7E4F8",
                      },
                    }}
                    onClick={() =>
                      handleViewFile(doc.uploadedFile.document_name)
                    }
                  >
                    <RemoveRedEyeOutlinedIcon />
                  </IconButton>

                  <IconButton
                    sx={{
                      background: "#FFFFFF",
                      borderRadius: "8px",
                      border: "1px solid #D0D5DD",
                      padding: "6px 8px",
                      "&:hover": {
                        backgroundColor: "#E7E4F8",
                      },
                    }}
                    onClick={() => handleDelete(doc.uploadedFile)}
                  >
                    <CloseRoundedIcon />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const ClarificationUploadedDocuments: React.FC<
  ClarificationUploadedDocumentsProps
> = ({
  documents,
  applicationId,
  uploadedFiles,
  setUploadedFiles,
  forceFreshUpload = false,
}) => {
  const dispatch = useDispatch<AppDispatch>(); 

  const [refreshImagesFlag, setRefreshImagesFlag] = React.useState(false);
  const [searchParams] = useSearchParams();
  const [uploadingDocs, setUploadingDocs] = React.useState<number[]>([]);

  const accessId = searchParams.get("access");
  const rowId = searchParams.get("row");

  React.useEffect(() => {
    if (forceFreshUpload) {
      setUploadedFiles([]);
      return;
    }

    const existingFiles =
      documents
        ?.filter((doc) => doc.uploadedFile)
        ?.map((doc) => ({
          ...doc.uploadedFile,
          document_id: doc.document_id,
          file_name: doc.uploadedFile.file_name,
          document_name: doc.uploadedFile.document_name,
          id: doc.uploadedFile.id,
        })) || [];

    setUploadedFiles(existingFiles);
  }, [documents, forceFreshUpload, setUploadedFiles]);

  const handleUpload = async (file: File, doc: any) => {
    if (!file) return;

    if (doc.media_type === "image") {
      const allowedTypes = ["image/jpeg", "image/png"];

      if (!allowedTypes.includes(file.type)) {
        toast.error("Only JPG, JPEG and PNG files are allowed");
        return;
      }
    }

    if (file.size > 21 * 1024 * 1024) {
      toast.error("File size must be less than 20 MB");
      return;
    }

    const fileUploadURL = await dispatch(
      genericUploadThunk({
        file,
        moduleType: accessId ? "access" : "row",
      }),
    );

    if (!fileUploadURL?.payload?.success) {
      toast.error("Upload failed");
      return;
    }

    if (!productionCheck) {
      await dispatch(
        uploadToS3Thunk({
          signedUrl: fileUploadURL.payload.data.signedUrl,
          file,
          contentType: file.type,
        }),
      );
    }

    setUploadingDocs((prev) => [...prev, doc.document_id]);

    try {
      await dispatch(
        postSaveUploadedFileThunk({
          application_id: applicationId,
          document_id: doc.document_id,
          document_type: "FILE_UPLOAD",
          fileName: fileUploadURL.payload.data.fileName,
          file_name: file.name,
          originalFileName: fileUploadURL.payload.data.fileName,
          objectKey: fileUploadURL.payload.data.objectKey,
          moduletype: accessId ? "access" : "row",
          isUndertaking: false,
        }),
      );

      setRefreshImagesFlag((prev) => !prev);

      setUploadedFiles((prev: any) => [
        ...prev,
        {
          document_id: doc.document_id,
          file_name: file.name,
          document_name: fileUploadURL.payload.data.fileName,
          objectKey: fileUploadURL.payload.data.objectKey,
          id: doc.document_id,
        },
      ]);
    } finally {
      setUploadingDocs((prev) => prev.filter((id) => id !== doc.document_id));
    }
  };

  const handleViewFile = (documentName: string) => {
    openFileBySignedUrl({
      document_name: documentName,
      dispatch,
      getSignedUrlThunk: accessId
        ? getFileSignedURLThunk
        : getFileROWSignedURLThunk,
    });
  };

  const handleDelete = async (uploadedFile: any) => {
    const deleteSignURL = await dispatch(
      getDeleteSignedURLThunk(uploadedFile.document_name),
    );

    const signedUrl = deleteSignURL.payload?.data?.signedUrl;

    if (signedUrl) {
      await deleteFileOnAwsAPI(signedUrl);

      await dispatch(
        deleteFileByIdThunk({
          access_id: applicationId,
          document_id: uploadedFile.id,
        }),
      );

      setUploadedFiles((prev: any[]) =>
        prev.filter((item) => item.document_id !== uploadedFile.document_id),
      );

      setRefreshImagesFlag((prev) => !prev);
    }
  };

  const mergedDocuments = documents.map((doc) => {
    const localUploadedFile = uploadedFiles.find(
      (file: any) => file.document_id === doc.document_id,
    );

    return {
      ...doc,
      uploadedFile: localUploadedFile || null,
    };
  });

  const drawingDocs = mergedDocuments.filter((doc) => doc.media_type === "drw");

  const documentDocs = mergedDocuments.filter(
    (doc) => doc.media_type === "pdf",
  );

  const imageDocs = mergedDocuments.filter((doc) => doc.media_type === "image");

  const isUploadInProgress = uploadingDocs.length > 0;

  return (
    <Box sx={{ width: "100%" }}>
      {drawingDocs.length > 0 && (
        <StaticTable
          title="Drawing type (PDF / JPEG)"
          docs={drawingDocs}
          handleUpload={handleUpload}
          handleDelete={handleDelete}
          handleViewFile={handleViewFile}
        />
      )}
      {documentDocs.length > 0 && (
        <StaticTable
          title="Document type (PDF / JPEG)"
          docs={documentDocs}
          handleUpload={handleUpload}
          handleDelete={handleDelete}
          handleViewFile={handleViewFile}
        />
      )}
      {imageDocs.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#212121",
              mb: 2,
            }}
          >
            Upload Plot Pictures<span className="required">*</span>
          </Typography>

          {/* Existing uploaded images */}
          {rowId ? (
            <ImagePreviewGridRow
              refreshTrigger={refreshImagesFlag}
              visibleIcons={true}
              onDeleteSuccess={() => {
                setRefreshImagesFlag((prev) => !prev);
              }}
            />
          ) : (
            <ImagePreviewGrid
              refreshTrigger={refreshImagesFlag}
              visibleIcons={true}
              onDeleteSuccess={() => {
                setRefreshImagesFlag((prev) => !prev);
              }}
            />
          )}

          {/* Upload image box */}
          {imageDocs.map((doc) => (
            <Box
              key={doc.document_id}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: "pointer",
                mt: 3,
                border: "1px dashed #CFDAE7",
                borderRadius: "8px",
                p: 2,
                background: "#F0EDF4",
                mb: 3,
                "&:hover": {
                  backgroundColor: "#E7E4F8",
                  transition: "all 0.3s ease-in-out",
                  borderColor: "#3A1C73",
                },
              }}
              component="label"
            >
              <input
                hidden
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={(e) => handleUpload(e.target.files?.[0] as File, doc)}
              />

              <AddPhotoAlternateOutlinedIcon
                sx={{
                  fontSize: 24,
                  color: "#3A1C73",
                }}
              />

              <Typography
                sx={{
                  color: "#3A1C73",
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                Click to add Photos
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
